package com.arena_web.utils

import kotlinx.browser.document
import kotlinx.coroutines.await
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.Image
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import kotlin.coroutines.resume
import kotlin.js.Promise
import kotlin.math.max
import kotlin.math.round
import kotlinx.coroutines.suspendCancellableCoroutine

// Event-gallery photos are viewed on screens, not printed: 1920px covers a
// phone's retina lightbox with headroom, and JPEG 0.85 is visually
// near-lossless — together they cut a 5–15 MB camera shot to ~200–500 KB.
private const val MAX_DIMENSION = 1920.0
private const val JPEG_QUALITY = 0.85

// Small originals aren't worth recoding — the JPEG round-trip would only
// shave a little (or even grow the file) while burning CPU on the phone.
private const val SKIP_BELOW_BYTES = 600 * 1024

/**
 * Downscales/recompresses an image on the client before upload. Returns the
 * new bytes + content type ("image/jpeg"), or the original pair untouched
 * when the input isn't an image, is already small, or anything in the
 * canvas pipeline fails (fail-open — worst case we upload the original,
 * same as before).
 */
suspend fun compressImageForUpload(
    bytes: ByteArray,
    contentType: String,
): Pair<ByteArray, String> {
    if (!contentType.startsWith("image/")) return bytes to contentType
    return try {
        val blob = Blob(
            arrayOf(bytes.unsafeCast<Int8Array>()),
            BlobPropertyBag(type = contentType),
        )
        val objectUrl = URL.createObjectURL(blob)
        try {
            val img = Image()
            val loaded = suspendCancellableCoroutine<Boolean> { cont ->
                img.addEventListener("load", { cont.resume(true) })
                img.addEventListener("error", { cont.resume(false) })
                img.src = objectUrl
            }
            if (!loaded) return bytes to contentType

            val w = img.naturalWidth
            val h = img.naturalHeight
            if (w <= 0 || h <= 0) return bytes to contentType
            val scale = (MAX_DIMENSION / max(w, h)).coerceAtMost(1.0)

            // Already screen-sized and reasonably light → keep as is.
            if (scale >= 1.0 && bytes.size <= SKIP_BELOW_BYTES) return bytes to contentType

            val canvas = document.createElement("canvas") as HTMLCanvasElement
            canvas.width = max(1, round(w * scale).toInt())
            canvas.height = max(1, round(h * scale).toInt())
            val ctx = canvas.getContext("2d") as? CanvasRenderingContext2D
                ?: return bytes to contentType
            ctx.drawImage(img, 0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())

            val outBlob = suspendCancellableCoroutine<Blob?> { cont ->
                canvas.asDynamic().toBlob(
                    { b: Blob? -> cont.resume(b) },
                    "image/jpeg",
                    JPEG_QUALITY,
                )
            } ?: return bytes to contentType

            val buffer = outBlob.asDynamic().arrayBuffer()
                .unsafeCast<Promise<ArrayBuffer>>()
                .await()
            val out = Int8Array(buffer).unsafeCast<ByteArray>()

            // A rare PNG-with-flat-colors can recode larger — keep the original.
            if (out.size >= bytes.size) bytes to contentType else out to "image/jpeg"
        } finally {
            URL.revokeObjectURL(objectUrl)
        }
    } catch (e: Throwable) {
        console.warn("compressImageForUpload failed, uploading original: ${e.message}")
        bytes to contentType
    }
}
